import PropTypes from 'prop-types';
import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Components, LinkTargets, ViewComponents } from '@powdr/constants';
import { getExternalNodeData } from '@powdr/stores/actions/node-action';
import {
  aliasContentBlockDataFromViews, componentFactory,
  getExternalEndpointContext, getExternalEndpointItems,
} from '@powdr/utils';

import { StyledDynamicComponent, DynamicLoader } from './styles';

export const DynamicComponent = ({
  id,
  blockType,
  columnOption = 2,
  columnOptionMobile = 1,
  componentType,
  itemsPerPage = null,
  colorProfile = null,
  columnGap,
  rowGap,
  gridPaddingTop,
  gridPaddingBottom,
  filterName,
  verticalAlignAll,
  alternateButtonStyles,
  isCenterAlignUnevenRows,
  noResultsMessage,
  externalEndpoint,
  slidesToShow,
  isHorizontalScroll,
}) => {
  const [isExternalDataLoader, setIsExternalDataLoader] = useState(!!(externalEndpoint));
  const dispatch = useDispatch();
  const { node } = useSelector((state) => state.node) || [];
  const externalItems = useMemo(
    () => getExternalEndpointItems(node?.[externalEndpoint]),
    [node, externalEndpoint],
  );
  const externalEndpointContext = useMemo(
    () => getExternalEndpointContext(externalEndpoint),
    [externalEndpoint],
  );

  const getExternalData = useCallback(() => {
    dispatch(getExternalNodeData(externalEndpoint));
    setIsExternalDataLoader(true);
  }, [dispatch, externalEndpoint]);

  useEffect(() => {
    if (externalItems) setIsExternalDataLoader(false);
  }, [externalItems]);

  // kickoff fetch of external data
  useEffect(() => {
    if (externalEndpoint) getExternalData();
  }, [externalEndpoint, getExternalData]);

  const componentData = useMemo(() => [{
    id,
    blockType,
    columnOption,
    columnOptionMobile,
    typeName: ViewComponents[componentType],
    itemsPerPage,
    colorProfile,
    columnGap,
    rowGap,
    gridPaddingTop,
    gridPaddingBottom,
    filterName,
    verticalAlignAll,
    alternateButtonStyles,
    isCenterAlignUnevenRows,
    noResultsMessage,
    isHorizontalScroll,
    slidesToShow,
    relationships: {
      contentBlocks: externalItems?.map((cb) => ({
        ...aliasContentBlockDataFromViews(
          cb,
          true,
          externalEndpointContext?.property,
          externalEndpointContext?.isDevelopment,
        ),
        typeName: Components.CONTENT_BLOCK,
        buttonTarget: LinkTargets.BLANK,
        altButtonTarget: LinkTargets.BLANK,
      })) || [],
    },
  }], [
    externalItems, externalEndpointContext, id, blockType,
    columnOption, columnOptionMobile, componentType,
    itemsPerPage, colorProfile,
    columnGap, rowGap,
    gridPaddingTop, gridPaddingBottom,
    filterName,
    verticalAlignAll,
    alternateButtonStyles,
    isCenterAlignUnevenRows,
    isHorizontalScroll,
    noResultsMessage,
    slidesToShow,
  ]);

  return (
    <StyledDynamicComponent isLoading={isExternalDataLoader}>
      {(isExternalDataLoader) ? <DynamicLoader /> : componentFactory(componentData)}
    </StyledDynamicComponent>
  );
};

DynamicComponent.propTypes = {
  id: PropTypes.string.isRequired,
  blockType: PropTypes.string.isRequired,
  columnOption: PropTypes.number,
  columnOptionMobile: PropTypes.number,
  componentType: PropTypes.string.isRequired,
  itemsPerPage: PropTypes.number,
  colorProfile: PropTypes.number,
  columnGap: PropTypes.number,
  rowGap: PropTypes.number,
  gridPaddingTop: PropTypes.number,
  gridPaddingBottom: PropTypes.number,
  filterName: PropTypes.string,
  verticalAlignAll: PropTypes.string,
  alternateButtonStyles: PropTypes.arrayOf(PropTypes.string),
  isCenterAlignUnevenRows: PropTypes.bool,
  externalEndpoint: PropTypes.string.isRequired,
  isHorizontalScroll: PropTypes.bool,
  slidesToShow: PropTypes.string,
  noResultsMessage: PropTypes.shape({
    value: PropTypes.string,
  }),
};
